import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'caffeineAmount', 'timeConsumed', 'metabolism', 'targetBedtime',
    'caffeineAtBedtime', 'percentAtBedtime', 'statusContainer', 'statusDot', 'statusText', 'statusDescription',
    'timeTo25', 'halfLife', 'cutoffTime', 'timeline'
  ]

  // Half-life values in hours
  static HALF_LIVES = {
    slow: 7,
    normal: 5,
    fast: 3
  }

  connect() {
    this.calculate()
  }

  calculate() {
    const caffeineAmount = parseFloat(this.caffeineAmountTarget.value) || 0
    const timeConsumed = this.timeConsumedTarget.value
    const metabolism = this.metabolismTarget.value
    const targetBedtime = this.targetBedtimeTarget.value

    const halfLife = this.constructor.HALF_LIVES[metabolism]
    this.halfLifeTarget.textContent = halfLife

    // Calculate time difference between consumption and bedtime
    const consumedMinutes = this.timeToMinutes(timeConsumed)
    let bedtimeMinutes = this.timeToMinutes(targetBedtime)

    // Handle bedtime being the next day
    if (bedtimeMinutes < consumedMinutes) {
      bedtimeMinutes += 24 * 60
    }

    const hoursUntilBedtime = (bedtimeMinutes - consumedMinutes) / 60

    // Calculate caffeine remaining at bedtime
    const caffeineAtBedtime = this.calculateCaffeineRemaining(caffeineAmount, hoursUntilBedtime, halfLife)
    const percentAtBedtime = caffeineAmount > 0 ? (caffeineAtBedtime / caffeineAmount) * 100 : 0

    this.caffeineAtBedtimeTarget.textContent = Math.round(caffeineAtBedtime)
    this.percentAtBedtimeTarget.textContent = Math.round(percentAtBedtime)

    // Calculate time to reach 25% (good for sleep)
    const hoursTo25 = this.calculateTimeToPercent(25, halfLife)
    const timeTo25Minutes = consumedMinutes + (hoursTo25 * 60)
    this.timeTo25Target.textContent = this.minutesToTime(timeTo25Minutes % (24 * 60))

    // Calculate recommended cutoff time
    const hoursNeeded = this.calculateTimeToPercent(25, halfLife)
    let cutoffMinutes = bedtimeMinutes - (hoursNeeded * 60)
    if (cutoffMinutes < 0) cutoffMinutes += 24 * 60
    this.cutoffTimeTarget.textContent = this.minutesToTime(cutoffMinutes % (24 * 60))

    // Update status
    this.updateStatus(caffeineAtBedtime, percentAtBedtime)

    // Update timeline
    this.updateTimeline(caffeineAmount, consumedMinutes, halfLife)
  }

  calculateCaffeineRemaining(initial, hours, halfLife) {
    // Using exponential decay formula: C(t) = C0 * (0.5)^(t/half-life)
    return initial * 0.5 ** (hours / halfLife)
  }

  calculateTimeToPercent(targetPercent, halfLife) {
    // Solving for t: targetPercent/100 = 0.5^(t/halfLife)
    // t = halfLife * log2(100/targetPercent)
    return halfLife * Math.log2(100 / targetPercent)
  }

  timeToMinutes(timeStr) {
    if (!timeStr) return 0
    const [hours, mins] = timeStr.split(':').map(Number)
    return hours * 60 + mins
  }

  minutesToTime(mins) {
    mins = Math.round(mins)
    while (mins < 0) mins += 24 * 60
    mins %= (24 * 60)
    const hours = Math.floor(mins / 60)
    const minutes = mins % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  updateStatus(caffeineAtBedtime, percentAtBedtime) {
    let dotColor; let statusText; let
      description

    if (percentAtBedtime <= 25) {
      dotColor = 'bg-green-500'
      statusText = 'OPTIMAL FOR SLEEP'
      description = 'Caffeine levels should be low enough for quality sleep. You should experience minimal sleep disruption.'
    } else if (percentAtBedtime <= 50) {
      dotColor = 'bg-yellow-500'
      statusText = 'MODERATE LEVELS'
      description = 'Some caffeine will remain at bedtime. You may experience slightly reduced sleep quality.'
    } else if (percentAtBedtime <= 75) {
      dotColor = 'bg-orange-500'
      statusText = 'HIGH LEVELS'
      description = 'Significant caffeine will remain at bedtime. This is likely to impact your sleep quality and duration.'
    } else {
      dotColor = 'bg-red-500'
      statusText = 'VERY HIGH LEVELS'
      description = 'Most caffeine will still be in your system at bedtime. Expect significant sleep disruption.'
    }

    this.statusDotTarget.className = `w-3 h-3 ${dotColor}`
    this.statusTextTarget.textContent = statusText
    this.statusDescriptionTarget.textContent = description
  }

  updateTimeline(initial, consumedMinutes, halfLife) {
    const timePoints = [0, 1, 2, 3, 4, 5, 6, 8, 10, 12]
    const entries = timePoints.map((hours) => {
      const remaining = this.calculateCaffeineRemaining(initial, hours, halfLife)
      const percent = initial > 0 ? (remaining / initial) * 100 : 0
      const time = this.minutesToTime((consumedMinutes + hours * 60) % (24 * 60))

      // Calculate bar width
      const barWidth = Math.max(2, percent)

      // Determine bar color
      let barColor = 'bg-red-500'
      if (percent <= 25) barColor = 'bg-green-500'
      else if (percent <= 50) barColor = 'bg-yellow-500'
      else if (percent <= 75) barColor = 'bg-orange-500'

      return `
        <div class="flex items-center gap-3">
          <span class="w-12 text-xs font-mono text-gray-500">${time}</span>
          <div class="flex-1 h-4 bg-gray-200 overflow-hidden">
            <div class="${barColor} h-full transition-all duration-300" style="width: ${barWidth}%"></div>
          </div>
          <span class="w-16 text-xs font-mono text-right">${Math.round(remaining)}mg</span>
          <span class="w-12 text-xs text-gray-500 text-right">${Math.round(percent)}%</span>
        </div>
      `
    })

    this.timelineTarget.innerHTML = entries.join('')
  }

  setAmount(event) {
    const amount = event.currentTarget.dataset.amount
    this.caffeineAmountTarget.value = amount
    this.calculate()
  }
}
